import { ColDef } from "ag-grid-community";
import { action, makeObservable, observable } from "mobx";
import PermissionsContext from "../../../../../../contexts/permissions/PermissionsContext";

import { EntityTypes } from "../../../../../../enums";
import ToasterService, { ToasterService as IToasterService } from "../../../../../toaster/ToasterService";
import { CommonColDefFieldNamesEnum } from "../../../enums/AgGridColDefFieldNameEnum";
import { ColumnStateAddon } from "../base/addons/ColumnStateAddon";
import { FilterStoreAddon } from "../base/addons/FilterAddon/FilterStoreAddon";
import { TextWrapperAddon } from "../base/addons/TextWrapAddon";
import { AppGridState } from "../base/AppGrid_view";
import { IGridUiAction } from "../base/AppGridToolbarActions_view";
import { BusinessAreasGridColumnBuilder } from "./BusinessAreasGridView_columns";
import { GetBusinessAreasGridActions } from "./BusinessAreasGridView_actions";
import OrganisationsApi, {
  OrganisationsApi as IOrganisationsApi
} from "../../../../../../services/api/v2/organisations/Organisations.api";
import BusinessAreasApi, {
  BusinessAreasApi as IBusinessAreasApi
} from "../../../../../../services/api/v2/businessAreas/BusinessAreas.api";

export class BusinessAreasGridModel {
  organisationId: number;
  authUser: FP.Entities.IUser;
  canEditCoreData: boolean;
  businessAreasGridColumnBuilder: BusinessAreasGridColumnBuilder;
  @observable selectedItems: number[] = [];
  @observable.ref gridActions: IGridUiAction[] = [];
  toasterService: IToasterService;
  organisationProvider: IOrganisationsApi;
  businessAreaProvider: IBusinessAreasApi;
  refreshDataHandler: any;
  @observable filterStoreAddon: FilterStoreAddon;
  @observable columnStateAddon: ColumnStateAddon;
  @observable textWrapAddon: TextWrapperAddon;
  @observable isFilterChanged: boolean;
  @observable isColumnStateChanged: boolean;
  @observable gridColumns: ColDef<any, any>[];
  onFieldUpdate: () => Promise<void>;

  constructor(
    organisationId: number,
    authUser: FP.Entities.IUser,
    refreshDataHandler: any,
    onFieldUpdate: () => Promise<void>
  ) {
    makeObservable(this);
    this.canEditCoreData = PermissionsContext.canContributeOrg(organisationId);
    this.organisationId = organisationId;
    this.authUser = authUser;
    this.toasterService = ToasterService;
    this.refreshDataHandler = refreshDataHandler;
    this.organisationProvider = OrganisationsApi;
    this.businessAreaProvider = BusinessAreasApi;
    this.onFieldUpdate = onFieldUpdate;
    this.generateAddons();
  }

  @action
  onMount = async () => {
    this.generateFn();
  };

  @action
  generateFn = () => {
    this.generateGridConfig();
    this.generateActions();
  };

  onUnmount = () => {};

  @action
  generateGridConfig = () => {
    this.businessAreasGridColumnBuilder = new BusinessAreasGridColumnBuilder({
      canEdit: true,
      organisationId: this.organisationId,
      onFieldUpdate: this.onFieldUpdate,
      columns: [CommonColDefFieldNamesEnum.Selected, CommonColDefFieldNamesEnum.Name]
    });
    this.gridColumns = this.businessAreasGridColumnBuilder.generateColumnDefs();
  };

  generateActions = () => {
    this.setGridActions(GetBusinessAreasGridActions(this));
  };

  @action
  generateAddons = () => {
    this.textWrapAddon = new TextWrapperAddon(null, EntityTypes.BUSINESS_AREAS);
    this.filterStoreAddon = new FilterStoreAddon({
      projectId: null,
      gridType: EntityTypes.BUSINESS_AREAS,
      filterHasChangedFn: this.setIsFilterChanged
    });
    this.columnStateAddon = new ColumnStateAddon({
      projectId: null,
      gridType: EntityTypes.BUSINESS_AREAS,
      columnOrderHasChangedFn: this.setIsColumnStateChanged
    });
  };

  @action
  setGridActions = (gridActions: any) => {
    this.gridActions = gridActions;
  };

  @action
  onGridStateUpdate = (gridState: AppGridState) => {
    this.selectedItems = gridState.selectedItems || [];
  };

  @action
  setIsColumnStateChanged = (isColumnStateChanged: boolean) => {
    this.isColumnStateChanged = isColumnStateChanged;
    this.generateActions();
  };

  @action
  setIsFilterChanged = (isFilterChanged: boolean) => {
    this.isFilterChanged = isFilterChanged;
    this.generateActions();
  };
}
